<template>
  <span class="resource-list-section">
    <div class="resource-input-section">
      <div :class="{ inactive: !resources.length }" class="resource-list-container">
        <span v-if="!resources.length" class="no-resources-message">
          {{ $t('newProject.no.targets.added.yet') }}
        </span>
        <div v-for="(res, index) in resources" :key="res.key">
          <div class="resource-divider" :class="{ 'non-first-padding': index > 0 }">
            <label>{{ $t('newProject.resources.resource') }} #{{ index + 1 }}</label>
            <v-icon class="resource-delete add-icon" @click="deleteResource(index)">delete_outline</v-icon>
          </div>
          <div class="current-resource">
            <span class="resource-value-section">
              <span class="resource-value-type-line">
                <label class="title-label sub-domain-title">
                  {{ $t('newProject.resources.addedResource') }}
                </label>
                <v-text-field
                  v-model="res.value"
                  @input="onResourceChange(res, res.value)"
                  type="text"
                  :placeholder="$t('newProject.placeholder.enter.target')"
                  class="form-control edit-added-resource"
                  outlined
                  dense
                  disabled
                />
              </span>
            </span>
            <div class="credentials-section line" v-if="configuration.data === TBT.GRAY_BOX">
              <div class="creds-section-header">
                <div class="section-title">{{ $t('newProject.resources.credentials') }}</div>
                <label class="title-label add-cred select-button" @click="addCredentialsForResource(res)">
                  +{{ $t('newProject.resources.add') }}
                </label>
              </div>

              <resource-credentials-form-row
                v-for="(cred, indexCred) in res.credentials"
                :key="cred.key"
                :credential="cred"
                :index="index"
                :deletable="res.credentials.length > 1"
                @delete="deleteCurrentCred(res, indexCred)"
              />
            </div>
            <div class="feature-section line" :class="{ disabled: automatedScoping && !res.automaticallyScoped }">
              <div class="feature-header">
                <div class="section-title">{{ $t('newProject.resources.features') }}</div>
                <div class="features-action clear" @click="clearAllFeatures(res)" v-if="showClearAllFeatures(res)">
                  {{ $t('newProject.resources.clearAll') }}
                </div>
                <div class="features-action select" @click="selectAllFeatures(res)" v-if="showSelectAllFeatures(res)">
                  {{ $t('newProject.resources.selectAll') }}
                </div>
              </div>
              <div class="feature-block" v-for="feature in res.features" :key="feature.value">
                <custom-checkbox
                  :value="feature.selected"
                  class="feature-checkbox"
                  color="cold-marine"
                  @input="
                    val => {
                      feature.selected = val;
                    }
                  "
                />
                <span class="feature-value">{{ $pt('newProject.resources.feature', feature.value) }}</span>
              </div>
              <div v-if="automatedScoping && !res.automaticallyScoped" class="overlay"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </span>
</template>

<script>
import { mapGetters } from 'vuex';
import { testBoxTypes } from '@/constants/projects';
import { correctDomainName } from '@/utils/stringUtils';

import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import ResourceCredentialsFormRow from '@/components/ProjectDetails/credentials/ResourceCredentialsFormRow.vue';

export default {
  name: 'NewProjectResourcesList',
  components: {
    CustomCheckbox,
    ResourceCredentialsFormRow,
  },
  props: {
    configuration: {
      type: Object,
      required: true,
    },
    resources: {
      type: Array,
      required: true,
    },
    automatedScoping: {
      type: Boolean,
      default: false,
    },
    automatedScopingRunning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editResourceListMode: false,
      allResourcesAreSelected: false,
    };
  },
  computed: {
    ...mapGetters(['domains']),
    TBT() {
      return testBoxTypes;
    },
  },
  methods: {
    onResourceChange(res, newValue) {
      // eslint-disable-next-line no-param-reassign
      res.value = correctDomainName(newValue);
      // eslint-disable-next-line no-param-reassign
      res.automaticallyScoped = false;
    },
    onCredChanged(res) {
      // eslint-disable-next-line no-param-reassign
      res.automaticallyScoped = false;
    },
    deleteCurrentCred(resource, index) {
      resource.credentials.splice(index, 1);
    },
    deleteResource(index) {
      this.resources.splice(index, 1);
    },
    addCredentialsForResource(resource) {
      resource.credentials.push({
        username: null,
        password: null,
        key: Math.random(),
      });
    },
    showSelectAllFeatures(resource) {
      const featuresLen = resource.features?.length;
      const nonSelectedLen = resource.features?.filter(f => f?.selected).length;
      return featuresLen !== nonSelectedLen;
    },
    showClearAllFeatures(resource) {
      const featuresLen = resource.features?.length;
      const selectedLen = resource.features?.filter(f => !f.selected).length;
      return featuresLen !== selectedLen;
    },
    selectAllFeatures(resource) {
      resource.features.forEach(f => {
        // eslint-disable-next-line no-param-reassign
        f.selected = true;
      });
    },
    clearAllFeatures(resource) {
      resource.features.forEach(f => {
        // eslint-disable-next-line no-param-reassign
        f.selected = false;
      });
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.resource-list-section {
  .resource-credentials-form-row {
    padding-right: rem(13);
  }
}
</style>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.resource-list-section {
  .resource-divider {
    background-color: var(--bg-primary);
    margin-left: rem(-37);
    padding-bottom: rem(13);

    &.non-first-padding {
      padding-top: rem(42);
    }

    label {
      color: var(--text-secondary);
      font-size: $font-size-sm;
      font-weight: 600;
      letter-spacing: 0.96px;
      line-height: rem(16);
      text-transform: uppercase;
    }
  }

  .title-label {
    float: left;
    height: rem(10);
    color: var(--text-secondary);
    font-size: $font-size-xs;
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-left: 0;
    margin-bottom: $space-3;
    text-align: left;
  }

  .sub-domain-title {
    padding-top: rem(5);
  }

  .resource-delete {
    cursor: pointer;
    float: right;
    color: var(--status-error);
    left: rem(-3);
    top: rem(3);
  }

  .resource-input-section {
    input {
      box-sizing: border-box;
      height: rem(32);
      width: rem(400);
      border: 1px solid var(--border-primary);
      border-radius: $radius-sm;
      background-color: var(--bg-primary);
      padding-left: $space-3;
    }
    input:focus,
    textarea:focus,
    button:focus {
      outline: none;
    }
    margin-bottom: rem(24);
  }

  .select-button {
    float: right;
    font-size: $font-size-sm;
    color: var(--brand-primary);
    cursor: pointer;
  }

  .resource-list-container {
    display: block;
    margin-top: rem(21);
    box-sizing: border-box;
    width: 100%;

    border-radius: $radius-md;
    background-color: var(--bg-secondary);

    padding-left: rem(37);

    &.inactive {
      background-color: var(--bg-secondary);
    }

    overflow: hidden;
    overflow-y: auto;

    .current-resource {
      text-align: left;
      margin-top: $space-3;
      display: flex;
      flex-direction: column;

      .resource-value-section {
        display: flex;
      }

      .resource-value-type-line {
        overflow: hidden;
        overflow-x: auto;

        .edit-added-resource {
          width: rem(452);
          height: rem(32);
          margin-bottom: rem(20);
        }
      }

      .resource-value {
        color: var(--text-primary);
        white-space: pre;
      }

      input {
        display: inline-block;
        width: rem(20);
        height: rem(20);
        margin-right: rem(9);
        left: rem(1);
      }
    }

    .no-resources-message {
      height: rem(48);
      color: var(--text-secondary);
      font-size: $font-size-sm;
      letter-spacing: 0;
      line-height: rem(16);
      text-align: center;

      display: block;
      margin-top: rem(106);
      margin-bottom: rem(106);
      margin-left: 25%;
      width: 50%;
    }
  }

  .section-title {
    text-transform: capitalize;
    color: var(--text-tertiary);
    font-size: $font-size-base;
    letter-spacing: 0;
    line-height: rem(16);
    margin-bottom: rem(15);
  }

  .credentials-section {
    margin-top: rem(11);

    .creds-section-header {
      padding-right: $space-2;
    }

    .resource-credential {
      display: block;
    }

    .creds-data {
      padding-right: $space-1;
    }

    .delete-cred {
      display: inline-block;
      float: right;
      top: rem(5);
      right: $space-2;
      position: relative;
      color: var(--status-error);
      cursor: pointer;
    }

    .cred-title {
      float: none;
      position: absolute;
    }

    input {
      display: inline-block;
      width: rem(220);
      height: rem(32);
      margin-bottom: $space-2;
    }

    .add-cred {
      float: right;
      position: relative;
      right: rem(34);
      top: rem(-27);
      margin-bottom: 0;
    }
  }

  .feature-section {
    width: rem(450);
    padding-bottom: rem(25);
    position: relative;

    .feature-header {
      div {
        display: inline-block;
      }

      .features-action {
        font-size: $font-size-sm;
        font-weight: 500;
        letter-spacing: 1.07px;
        line-height: rem(16);
        text-align: center;
        text-transform: uppercase;
        cursor: pointer;

        &.select {
          color: var(--brand-secondary);
          float: right;
        }
        &.clear {
          color: var(--status-error);
          float: right;
          margin-left: rem(15);
        }
      }
    }

    .feature-block {
      width: 50%;
      display: inline-block;
      margin-bottom: $space-2;

      .feature-checkbox {
        display: inline-block;
      }
      .feature-value {
        color: var(--text-primary);
        font-size: $font-size-sm;
        letter-spacing: 0;
        line-height: rem(16);
      }
    }

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background: rgba(255, 255, 255, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &.disabled {
      .features-action {
        opacity: 0.3;
      }
    }
  }

  .line:before {
    content: '';
    width: rem(452);
    margin-top: rem(20);
    margin-bottom: rem(14);
    border-bottom: solid 1px var(--border-secondary);
    display: inline-block;
    left: 0;
    z-index: 1;
  }
}
</style>
